import { Routes, Route, Navigate } from 'react-router-dom'
import { Modality } from './component/modality/modality'
import { Home } from './component/home'
import { ModalityDetail } from './component/modality/modality-detail'
import { AddModality } from './component/modality/add-modality'
import { EditModality } from './component/modality/edit-modality'
import { Amplify } from 'aws-amplify'
import { amplifyConfig } from '../env-exports'
import { useEffect, useState } from 'react'
import { signOut } from 'aws-amplify/auth'
import Header from './component/header'
import Footer from './component/footer'
import { MaintenanceBanner } from './component/maintenance-banner'
// import { Toaster } from './component/ui/toaster';
import HelpSection from './component/help'
import { Sidebar } from './component/sidebar'
import { getHeaderLinks } from './utils/nav-utils'
import { UserSession } from './types/user-session'
import { userSessionProvider, signInUser } from './providers/user-session-provider'
import AuthCallback from './services/auth_callback'
import { ResourceFile } from './component/resourcefile'
import { Cluster } from './component/cluster/cluster'
import { ClusterDetail } from './component/cluster/cluster-detail'
import { isResearcher, getUserName, getStudentId, isStudent } from './utils/user-utils'
import { QuestionnaireIntro } from './component/student/intro'
import { SurveyResultsDashboard } from './component/student/studentdashboard'
import { SurveyQuestionPage } from './component/student/survey-question-page'
import { SurveyResultsWrapper } from './component/student/survey-result-wrapper'
import { hasAdminGroup } from './utils/user-utils'
import { SignOutPage } from './component/signout'
import { useHeaderStore } from './component/store/header-store'
import { NotFound } from './component/not-found'
import { getLatestSurveyAttempt } from './services/survey'
import { useSidebarStore } from './component/store/sidebare-store'
import { useJsonStore } from './component/store/json-store'

Amplify.configure({ ...amplifyConfig })

export default function App() {
  const [wasLoggedOut, setWasLoggedOut] = useState<boolean | false>(false)
  const [session, setSession] = useState<UserSession | undefined>(undefined)
  const [userInfo, setUserInfo] = useState<{
    userName: string
    studentId: string
  } | null>(null)
  const [completedSurveyId, setCompletedSurveyId] = useState<number | null | undefined>(undefined)
  const links = getHeaderLinks()

  useEffect(() => {
    ;(async () => {
      const userSession = await userSessionProvider()

      const wasLoggedOut = sessionStorage.getItem('logoutInitiated')

      if (!userSession && !wasLoggedOut) {
        console.log('Sign in process start')
        await signInUser()
      } else if (wasLoggedOut) {
        console.log('Redirected after logout')
        sessionStorage.removeItem('logoutInitiated')

        // Upon page refresh, state memory will be gone. No need to clear afterwards
        setWasLoggedOut(true)
      } else {
        setSession(userSession)
        // Extract both userName and studentId
        const name = await getUserName()
        const id = await getStudentId()
        setUserInfo({ userName: name, studentId: id })

        // Pre-check if this student already has a completed survey so the intro
        // page is never briefly shown before redirecting to results.
        const parsedGroups =
          userSession?.userGroups
            .replace(/^\[|\]$/g, '')
            .split(',')
            .map((g) => g.trim()) ?? []

        if (isStudent(parsedGroups) && !isResearcher(parsedGroups) && !hasAdminGroup(parsedGroups)) {
          try {
            const attempt = await getLatestSurveyAttempt(id)
            if (attempt?.surveyId) {
              useSidebarStore.getState().setSidebarState({ surveyResult: true })
              useJsonStore.getState().setGenericObject({ surveyId: attempt.surveyId })
              localStorage.setItem('currentSurveyId', String(attempt.surveyId))
              setCompletedSurveyId(attempt.surveyId)
            } else {
              setCompletedSurveyId(null)
            }
          } catch {
            // No completed attempt — show intro as normal
            setCompletedSurveyId(null)
          }
        } else {
          setCompletedSurveyId(null)
        }

        // Debug logging (remove in production)
        // console.log('User info extracted:', {
        //   userName: name,
        //   studentId: id,
        //   studentIdLength: id.length,
        //   isUUID: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id),
        // })
      }
    })()
  }, [])

  // console.log('session?.userGroups: ', session?.userGroups)
  const parsedUserGroup = session?.userGroups
    .replace(/^\[|\]$/g, '') // remove brackets
    .split(',') // split by comma
    .map((g) => g.trim()) // trim whitespace

  // const isAdmin = hasAdminGroup(session?.userGroups.split(', ') ?? [])
  const isAdmin = isResearcher(parsedUserGroup ?? []) || hasAdminGroup(parsedUserGroup ?? [])
  const isUserStudent = isStudent(parsedUserGroup ?? [])

  const signOutFromAD = async () => {
    console.log('signOutFromAD called: START')
    try {
      sessionStorage.setItem('logoutInitiated', 'true')

      // Ensure all tokens are revoked
      await signOut({ global: true })

      // Add a short delay to ensure session is cleared
      await new Promise((resolve) => setTimeout(resolve, 1000))

      // Redirect the user to a clean entry point
      // navigate('/signout')
    } catch (error) {
      console.error('Error signing out: ', error)
    }
  }

  if (wasLoggedOut) {
    // This is a workaround to handle signout regardless if cognito idp failed on redirection
    useHeaderStore.getState().setHeaderState({
      showHeaderMenu: false,
    })

    return (
      <div>
        <div className='flex min-h-screen flex-col bg-white'>
          <Header navLinks={links} userName={''} signOutFromAD={signOutFromAD} />
          <section className='w-full h-screen bg-[var(--header)] flex items-center justify-center'>
            <h1 className='text-3xl font-bold text-center'>Thank you for using Janoos!</h1>
          </section>
          <HelpSection />
          <Footer />
        </div>
      </div>
    )
  }

  // Wait for session, user info, and the student survey pre-check to all resolve
  if (!session || !userInfo || completedSurveyId === undefined) return <p>Loading...</p>

  const { userName, studentId } = userInfo

  // Invalid user catch
  if (session && !isAdmin && !isUserStudent) {
    return (
      <div>
        <div className='flex min-h-screen flex-col bg-white'>
          <Header navLinks={links} userName={userName} signOutFromAD={signOutFromAD} />
          <div className='flex min-h-[1000px] flex-grow'>
            {/* Invalid user state */}
            <p>Error Logging in. Please contact admin for assistance.</p>
          </div>
          <HelpSection isStudent={false} />
          <Footer />
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className='flex min-h-screen flex-col bg-white'>
        <Header navLinks={links} userName={userName} signOutFromAD={signOutFromAD} />
        <MaintenanceBanner />
        <div className='flex min-h-[1000px] flex-grow'>
          <Sidebar isAdmin={isAdmin} />
          {/* <Toaster /> */}
          <Routes>
            <Route path='/signout' element={<SignOutPage />} />
            {/* Admin Routes */}
            {isAdmin && (
              <>
                <Route path='/' element={<Home userName={userName} />} />
                <Route path='/resourcefile' element={<ResourceFile />} />
                <Route path='/clusters' element={<Cluster />} />
                <Route path='/clusters/:id' element={<ClusterDetail />} />
                <Route path='/modalities' element={<Modality />} />
                <Route path='/modalities/new' element={<AddModality />} />
                <Route path='/modalities/:id' element={<ModalityDetail />} />
                <Route path='/modalities/:id/edit' element={<EditModality />} />
              </>
            )}
            {/* Student Routes */}
            {!isAdmin && (
              <>
                <Route
                  path='/'
                  element={
                    completedSurveyId ? <Navigate to='/student/survey/results' replace /> : <QuestionnaireIntro userName={userName} studentId={studentId} />
                  }
                />
                <Route
                  path='/student'
                  element={
                    completedSurveyId ? <Navigate to='/student/survey/results' replace /> : <QuestionnaireIntro userName={userName} studentId={studentId} />
                  }
                />
                <Route
                  path='/student/survey/intro'
                  element={
                    completedSurveyId ? <Navigate to='/student/survey/results' replace /> : <QuestionnaireIntro userName={userName} studentId={studentId} />
                  }
                />
                <Route path='/student/survey/question/:questionNumber' element={<SurveyQuestionPage userName={userName} studentId={studentId} />} />
                <Route path='/student/survey/results' element={<SurveyResultsWrapper userName={userName} studentId={studentId} />} />
                <Route path='/dashboard' element={<SurveyResultsDashboard userName={userName} />} />
              </>
            )}

            {/* Common Routes - Available to both */}
            <Route path='/auth_callback' element={<AuthCallback />} />

            {/* Catch-all route for 404 - Must be last */}
            <Route path='*' element={<NotFound />} />
          </Routes>
        </div>
        <HelpSection isStudent={isUserStudent} />
        <Footer />
      </div>
    </div>
  )
}
